import React, { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { process as defaultSteps } from "../data/mock";

const Process = ({ 
  steps = defaultSteps, 
  title = "From first spark to final frame", 
  kicker = { index: "05", label: "Our Craft Process" },
  id = "process"
}) => {
  const containerRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  return (
    <>
      {/* Desktop Version */}
      <section
        id={id}
        ref={containerRef}
        className="relative hidden lg:block"
        style={{ height: `${steps.length * 100}vh`, background: "#0a0908" }}
      >
        <div className="sticky top-0 h-screen flex flex-col overflow-hidden">
          {/* Dynamic Background Images */}
          <div className="absolute inset-0 z-0">
            {steps.map((s, i) => (
              <ProcessBackgroundItem 
                key={`bg-${s.step}`}
                image={s.image}
                index={i}
                progress={smoothProgress}
                total={steps.length}
              />
            ))}
            {/* Global Overlays - Reduced intensity for better photo visibility */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0a0908] via-transparent to-[#0a0908] opacity-60" />
            <div className="absolute inset-0 bg-black/20" />
          </div>

          {/* Cinematic Background Elements */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_20%_30%,rgba(201,169,97,0.05)_0%,transparent_50%)]" />
            <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(circle_at_80%_70%,rgba(201,169,97,0.03)_0%,transparent_50%)]" />
          </div>

          {/* Header Section */}
          <div className="container-xl w-full pt-32 pb-10 z-10">
            <div className="max-w-4xl">
              <Kicker index={kicker.index} label={kicker.label} />
              <motion.h2
                className="mt-6 font-display text-[clamp(40px,6vw,86px)] leading-[0.95] text-[var(--cream)] balance"
              >
                {title.includes("spark") ? (
                  <>From first spark to <span className="italic gold-text">final frame</span></>
                ) : (
                  <span className="italic gold-text">{title}</span>
                )}
              </motion.h2>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 relative container-xl w-full z-10 flex items-center">
            <div className="grid grid-cols-12 gap-12 w-full">
              
              {/* Left side: Step Indicators (Sticky) */}
              <div className="col-span-4 flex flex-col justify-center gap-10">
                {steps.map((s, i) => (
                  <StepIndicator 
                    key={s.step} 
                    index={i} 
                    step={s.step} 
                    title={s.title}
                    progress={smoothProgress} 
                    total={steps.length}
                  />
                ))}
              </div>

              {/* Right side: Active Step Content */}
              <div className="col-span-8 relative h-[400px] flex items-center">
                {steps.map((s, i) => (
                  <StepContent 
                    key={s.step} 
                    index={i} 
                    title={s.title} 
                    desc={s.desc} 
                    progress={smoothProgress} 
                    total={steps.length}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Bottom Progress Bar */}
          <div className="container-xl w-full pb-10 z-10">
            <div className="flex items-center gap-6">
              <div className="font-impact text-[10px] tracking-[0.4em] text-[var(--gold)]">PROGRESS</div>
              <div className="flex-1 h-px bg-[rgba(201,169,97,0.1)] relative overflow-hidden">
                <motion.div 
                  className="absolute inset-0 bg-gradient-to-r from-[var(--gold)] to-[var(--gold-bright)]"
                  style={{ scaleX: smoothProgress, transformOrigin: "left" }}
                />
              </div>
              <div className="font-impact text-[10px] tracking-[0.4em] text-[var(--cream-muted)]">
                {Math.round(scrollYProgress.get() * 100)}%
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Version - Natural Sticky Stacking */}
      <section 
        id={`${id}-mobile`} 
        className="lg:hidden bg-[#0a0908] py-24 px-4"
      >
        <div className="container-xl px-0">
          <div className="mb-16">
            <Kicker index={kicker.index} label={kicker.label} />
            <h2 className="mt-6 font-display text-4xl leading-[1.1] text-[var(--cream)]">
              {title}
            </h2>
          </div>

          <div className="flex flex-col gap-[30vh]">
            {steps.map((s, i) => (
              <MobileStepCard 
                key={s.step} 
                step={s} 
                index={i} 
                total={steps.length} 
              />
            ))}
          </div>
          
          {/* Spacer to allow the last card to scroll past */}
          <div className="h-[20vh]" />
        </div>
      </section>
    </>
  );
};

const MobileStepCard = ({ step, index, total }) => {
  const cardRef = useRef(null);
  
  // Track scroll progress of THIS specific card to trigger effects when it hits the top
  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ["start end", "end start"]
  });

  // Scale down and dim the card as it scrolls past the sticky point
  // "0.5" is roughly when the card is centered/at its sticky position
  const scale = useTransform(scrollYProgress, [0.5, 0.8], [1, 0.92]);
  const opacity = useTransform(scrollYProgress, [0.5, 0.8], [1, 0.6]);
  const overlayOpacity = useTransform(scrollYProgress, [0.5, 0.8], [0, 0.6]);
  
  // Add a slight "fanned out" rotation like 3patti cards
  // Alternate rotations for a more organic feel
  const baseRotation = (index % 2 === 0 ? -1 : 1) * (1 + Math.random() * 2);

  return (
    <div
      ref={cardRef}
      className="sticky w-full"
      style={{ 
        top: `${120 + index * 20}px`,
        zIndex: index 
      }}
    >
      <motion.div
        style={{ 
          scale, 
          opacity,
          rotate: baseRotation
        }}
        className="relative aspect-[4/5] rounded-[2rem] overflow-hidden border border-white/10 shadow-[0_-20px_60px_rgba(0,0,0,0.8)] bg-[#0a0908]"
      >
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${step.image})`, 
            opacity: 0.5 
          }}
        />

        {/* Darkening Overlay as it gets covered */}
        <motion.div 
          className="absolute inset-0 bg-black"
          style={{ opacity: overlayOpacity }}
        />
        
        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

        {/* Content */}
        <div className="absolute inset-0 p-8 flex flex-col justify-end">
          <div className="flex items-center gap-4 mb-6">
            <span className="font-impact text-7xl opacity-10 text-white leading-none">{step.step}</span>
            <div className="w-12 h-px bg-[var(--gold)]" />
            <span className="font-impact text-[10px] tracking-[0.4em] text-[var(--gold)] uppercase">Phase {index + 1}</span>
          </div>
          
          <h3 className="font-display text-3xl text-white mb-4 leading-tight">
            {step.title}
          </h3>
          
          <p className="text-[var(--cream-muted)] text-[15px] leading-relaxed font-light">
            {step.desc}
          </p>
        </div>
      </motion.div>
    </div>
  );
};

const StepIndicator = ({ index, step, title, progress, total }) => {
  const start = index / total;
  const end = (index + 1) / total;
  
  // Safe ranges for indicators
  const range = [
    Math.max(0, start), 
    Math.min(1, start + 0.05), 
    Math.max(0, end - 0.05), 
    Math.min(1, end)
  ];
  
  const isActive = useTransform(progress, range, [0.3, 1, 1, 0.3]);
  const scale = useTransform(progress, range, [1, 1.1, 1.1, 1]);
  const color = useTransform(progress, [range[0], range[1]], ["var(--cream-muted)", "var(--gold)"]);
  
  return (
    <motion.div 
      style={{ opacity: isActive, scale }}
      className="flex items-center gap-6 group cursor-pointer"
    >
      <div className="relative">
        <div className="w-10 h-10 rounded-full border border-[var(--rule)] flex items-center justify-center font-impact text-sm">
          {step}
        </div>
        <motion.div 
          className="absolute -inset-1 rounded-full border border-[var(--gold)]"
          style={{ opacity: useTransform(progress, [range[0], range[0] + 0.05], [0, 1]) }}
        />
      </div>
      <motion.span 
        style={{ color }}
        className="font-impact text-xl tracking-[0.1em] uppercase"
      >
        {title}
      </motion.span>
    </motion.div>
  );
};

const StepContent = ({ index, title, desc, progress, total }) => {
  const start = index / total;
  const end = (index + 1) / total;
  
  // Content fades in and out as we scroll through its range
  const range = [
    Math.max(0, start - 0.05),
    Math.max(0.001, start),
    Math.min(0.999, end - 0.05),
    Math.min(1, end)
  ];

  const opacity = useTransform(progress, range, [0, 1, 1, 0]);
  const y = useTransform(progress, range, [40, 0, 0, -40]);

  return (
    <motion.div 
      style={{ opacity, y }}
      className="absolute inset-0 flex flex-col justify-center"
    >
      <div className="font-impact text-[120px] leading-none opacity-5 absolute -top-10 -left-10 select-none">
        0{index + 1}
      </div>
      <h3 className="font-display text-4xl md:text-5xl text-[var(--cream)] mb-6">
        {title}
      </h3>
      <p className="text-xl text-[var(--cream-muted)] leading-relaxed max-w-2xl font-light">
        {desc}
      </p>
      <div className="mt-10 flex items-center gap-4">
        <div className="w-12 h-px bg-[var(--gold)]" />
        <span className="font-impact text-[10px] tracking-[0.4em] text-[var(--gold)] uppercase">Phase {index + 1}</span>
      </div>
    </motion.div>
  );
};

const Kicker = ({ index, label }) => (
  <div className="flex items-center gap-4">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)]">{label.toUpperCase()}</span>
  </div>
);

const ProcessBackgroundItem = ({ image, index, progress, total }) => {
  const start = index / total;
  const mid = (index + 0.5) / total;
  const end = (index + 1) / total;
  
  const range = [
    Math.max(0, start - 0.05),
    Math.max(0.001, start),
    Math.min(0.999, mid),
    Math.min(1, end)
  ];

  const opacity = useTransform(progress, range, [0, 0.4, 0.4, 0]);

  return (
    <motion.div
      className="absolute inset-0 bg-cover bg-center"
      style={{
        backgroundImage: `url(${image})`,
        opacity,
        filter: "grayscale(0.4) brightness(0.6)"
      }}
    />
  );
};

export default Process;
